import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const LoginForm = ({ 
  authMethod, 
  currentLanguage, 
  onSubmit, 
  isLoading, 
  onForgotPassword,
  onSwitchToRegister 
}) => {
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    otpCode: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const mockCredentials = {
    email: "demo@autocasse.cm",
    phone: "677123456",
    password: "Demo123!",
    otp: "123456"
  };

  const validateForm = () => {
    const newErrors = {};

    if (authMethod === 'email') {
      if (!formData?.email) {
        newErrors.email = currentLanguage === 'fr' ? 'Email requis' : 'Email required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
        newErrors.email = currentLanguage === 'fr' ? 'Email invalide' : 'Invalid email';
      }
      
      if (!formData?.password) {
        newErrors.password = currentLanguage === 'fr' ? 'Mot de passe requis' : 'Password required';
      }
    } else if (authMethod === 'phone') {
      if (!formData?.phone) {
        newErrors.phone = currentLanguage === 'fr' ? 'Numéro requis' : 'Phone number required';
      } else if (!/^6[0-9]{8}$/?.test(formData?.phone)) {
        newErrors.phone = currentLanguage === 'fr' ? 'Numéro invalide (6XXXXXXXX)' : 'Invalid number (6XXXXXXXX)';
      }
      
      if (!formData?.password) {
        newErrors.password = currentLanguage === 'fr' ? 'Mot de passe requis' : 'Password required';
      }
    } else if (authMethod === 'whatsapp') {
      if (!formData?.phone) {
        newErrors.phone = currentLanguage === 'fr' ? 'Numéro WhatsApp requis' : 'WhatsApp number required';
      } else if (!/^6[0-9]{8}$/?.test(formData?.phone)) {
        newErrors.phone = currentLanguage === 'fr' ? 'Numéro invalide (6XXXXXXXX)' : 'Invalid number (6XXXXXXXX)';
      }
      
      if (otpSent && !formData?.otpCode) {
        newErrors.otpCode = currentLanguage === 'fr' ? 'Code OTP requis' : 'OTP code required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSendOTP = async () => {
    if (!formData?.phone || !/^6[0-9]{8}$/?.test(formData?.phone)) {
      setErrors({ phone: currentLanguage === 'fr' ? 'Numéro invalide' : 'Invalid number' });
      return;
    }

    setOtpSent(true);
    // Simulate OTP sending
    setTimeout(() => {
      alert(currentLanguage === 'fr' 
        ? `Code OTP envoyé au +237${formData?.phone} via WhatsApp. Code de test: ${mockCredentials?.otp}`
        : `OTP sent to +237${formData?.phone} via WhatsApp. Test code: ${mockCredentials?.otp}`
      );
    }, 1000);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    // Validate against mock credentials
    let isValid = false;
    let errorMessage = '';

    if (authMethod === 'email') {
      if (formData?.email === mockCredentials?.email && formData?.password === mockCredentials?.password) {
        isValid = true;
      } else {
        errorMessage = currentLanguage === 'fr' ?'Email ou mot de passe incorrect' :'Invalid email or password';
      }
    } else if (authMethod === 'phone') {
      if (formData?.phone === mockCredentials?.phone && formData?.password === mockCredentials?.password) {
        isValid = true;
      } else {
        errorMessage = currentLanguage === 'fr' ?'Numéro ou mot de passe incorrect' :'Invalid phone or password';
      }
    } else if (authMethod === 'whatsapp') {
      if (formData?.phone === mockCredentials?.phone && formData?.otpCode === mockCredentials?.otp) {
        isValid = true;
      } else {
        errorMessage = currentLanguage === 'fr' ?'Code OTP incorrect' :'Invalid OTP code';
      }
    }

    if (isValid) {
      onSubmit(formData);
    } else {
      setErrors({ general: errorMessage });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Demo Credentials Info */}
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <Icon name="Info" size={16} className="text-accent mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-accent mb-2">
              {currentLanguage === 'fr' ? 'Identifiants de démonstration' : 'Demo Credentials'}
            </p>
            <div className="space-y-1 text-muted-foreground">
              <p>Email: {mockCredentials?.email}</p>
              <p>{currentLanguage === 'fr' ? 'Téléphone' : 'Phone'}: {mockCredentials?.phone}</p>
              <p>{currentLanguage === 'fr' ? 'Mot de passe' : 'Password'}: {mockCredentials?.password}</p>
              <p>OTP: {mockCredentials?.otp}</p>
            </div>
          </div>
        </div>
      </div>
      {/* General Error */}
      {errors?.general && (
        <div className="flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
          <Icon name="AlertCircle" size={16} className="text-error" />
          <p className="text-sm text-error">{errors?.general}</p>
        </div>
      )}
      {/* Email Input */}
      {authMethod === 'email' && (
        <Input
          label={currentLanguage === 'fr' ? 'Adresse email' : 'Email Address'}
          type="email"
          placeholder={currentLanguage === 'fr' ? 'votre@email.com' : 'your@email.com'}
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          required
          description={currentLanguage === 'fr' ?'Entrez votre adresse email' :'Enter your email address'
          }
        />
      )}
      {/* Phone Input */}
      {(authMethod === 'phone' || authMethod === 'whatsapp') && (
        <Input
          label={authMethod === 'whatsapp' 
            ? (currentLanguage === 'fr' ? 'Numéro WhatsApp' : 'WhatsApp Number')
            : (currentLanguage === 'fr' ? 'Numéro de téléphone' : 'Phone Number')
          }
          type="tel"
          placeholder="6XXXXXXXX"
          value={formData?.phone}
          onChange={(e) => handleInputChange('phone', e?.target?.value)}
          error={errors?.phone}
          required
          description={currentLanguage === 'fr' ?'Format: 6XXXXXXXX (sans +237)' :'Format: 6XXXXXXXX (without +237)'
          }
        />
      )}
      {/* Password Input */}
      {(authMethod === 'email' || authMethod === 'phone') && (
        <div className="relative">
          <Input
            label={currentLanguage === 'fr' ? 'Mot de passe' : 'Password'}
            type={showPassword ? 'text' : 'password'}
            placeholder={currentLanguage === 'fr' ? 'Votre mot de passe' : 'Your password'}
            value={formData?.password}
            onChange={(e) => handleInputChange('password', e?.target?.value)}
            error={errors?.password}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-8 text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
          </button>
        </div>
      )}
      {/* WhatsApp OTP Section */}
      {authMethod === 'whatsapp' && (
        <div className="space-y-4">
          {!otpSent ? (
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={handleSendOTP}
              iconName="MessageCircle"
              iconPosition="left"
              iconSize={16}
              className="border-success text-success hover:bg-success hover:text-success-foreground"
            >
              {currentLanguage === 'fr' ? 'Envoyer le code OTP' : 'Send OTP Code'}
            </Button>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 p-3 bg-success/10 border border-success/20 rounded-lg">
                <Icon name="MessageCircle" size={16} className="text-success" />
                <p className="text-sm text-success">
                  {currentLanguage === 'fr' 
                    ? `Code envoyé au +237${formData?.phone}` 
                    : `Code sent to +237${formData?.phone}`
                  }
                </p>
              </div>
              
              <Input
                label={currentLanguage === 'fr' ? 'Code OTP' : 'OTP Code'}
                type="text"
                placeholder="123456"
                value={formData?.otpCode}
                onChange={(e) => handleInputChange('otpCode', e?.target?.value)}
                error={errors?.otpCode}
                required
                maxLength={6}
                description={currentLanguage === 'fr' ?'Entrez le code reçu sur WhatsApp' :'Enter the code received on WhatsApp'
                }
              />
              
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setOtpSent(false)}
                iconName="ArrowLeft"
                iconPosition="left"
                iconSize={14}
              >
                {currentLanguage === 'fr' ? 'Changer le numéro' : 'Change number'}
              </Button>
            </div>
          )}
        </div>
      )}
      {/* Submit Button */}
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={isLoading}
        disabled={authMethod === 'whatsapp' && !otpSent}
        iconName="LogIn"
        iconPosition="left"
        iconSize={16}
      >
        {currentLanguage === 'fr' ? 'Se connecter' : 'Sign In'}
      </Button>
      {/* Forgot Password */}
      {(authMethod === 'email' || authMethod === 'phone') && (
        <div className="text-center">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm text-primary hover:text-primary/80 transition-smooth"
          >
            {currentLanguage === 'fr' ? 'Mot de passe oublié ?' : 'Forgot password?'}
          </button>
        </div>
      )}
      {/* Register Link */}
      <div className="text-center pt-4 border-t border-border">
        <p className="text-sm text-muted-foreground mb-2">
          {currentLanguage === 'fr' ? 'Pas encore de compte ?' : "Don't have an account?"}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={onSwitchToRegister}
          iconName="UserPlus"
          iconPosition="left"
          iconSize={16}
        >
          {currentLanguage === 'fr' ? 'Créer un compte' : 'Create Account'}
        </Button>
      </div>
    </form>
  );
};

export default LoginForm;